import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Building2, CircleCheck as CheckCircle2, HardHat, MapPin, PhoneCall, ShieldCheck, Truck } from 'lucide-react';
export default function Hero() {
  const coverageAreas = [{
    name: "Berkshire"
  }, {
    name: "Surrey"
  }, {
    name: "Hampshire"
  }, {
    name: "Buckinghamshire"
  }, {
    name: "Greater London"
  }];
  const targetSectors = [{
    title: "Roofing & solar installers"
  }, {
    title: "Main contractors & developers"
  }, {
    title: "Councils & facilities managers"
  }, {
    title: "Builders & homeowners"
  }];
  const credentials = [{
    label: "NASC accredited member"
  }, {
    label: "CHAS certified contractor"
  }];
  return <section id="hero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground">
      {/* Background scaffolding grid subtle pattern overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,var(--color-primary)/15,transparent_55%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,var(--color-secondary))] pointer-events-none opacity-80" />

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Main Copy Column */}
          <div className="lg:col-span-7">
            {/* Accreditation tags */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-sidebar-border bg-sidebar text-sidebar-foreground px-3 py-1 font-medium text-xs tracking-wide">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>Safety-led scaffold contractor</span>
              </Badge>
              <span className="hidden text-xs text-muted-foreground sm:inline-block">
                •
              </span>
              <span className="text-xs text-secondary-foreground/80 font-medium">
                Home Counties &amp; South East
              </span>
            </div>

            {/* Main H1 Headline */}
            <h1 className="font-default text-3xl font-bold tracking-tight text-secondary-foreground sm:text-4xl md:text-5xl lg:text-5xl/tight">
              Scaffolding services for commercial, residential and specialist projects across the South East
            </h1>

            {/* Supporting body copy */}
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-secondary-foreground/85 sm:text-lg">
              BMF Scaffolding delivers compliant access solutions for roofing contractors, solar installers, main contractors, local authorities, commercial facilities, and residential homeowners. We combine strict safety governance with prompt, dependable site turnarounds.
            </p>

            {/* Coverage strip */}
            <div className="mt-6 flex flex-wrap items-center gap-y-2 gap-x-4 text-xs font-medium text-secondary-foreground/75">
              <span className="flex items-center gap-1.5 text-secondary-foreground">
                <MapPin className="size-4 text-primary" />
                <span>Primary coverage:</span>
              </span>
              {coverageAreas.map((area, i) => <span key={area.name} data-index={i} className="inline-flex items-center gap-1 rounded bg-sidebar-accent px-2 py-0.5 text-sidebar-accent-foreground">
                  {area.name}
                </span>)}
            </div>

            {/* Primary Action Buttons */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold text-base px-6 h-12">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-semibold text-base px-6 h-12">
                <a href="tel:01344929900" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call 01344 929900</span>
                </a>
              </Button>
            </div>

            {/* Trust credentials row */}
            <div className="mt-10 border-t border-sidebar-border pt-6">
              <p className="text-xs uppercase tracking-wider font-semibold text-secondary-foreground/60 mb-3">
                Verified site accreditations
              </p>
              <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                {credentials.map((cred, i) => <div key={cred.label} data-index={i} className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-secondary-foreground/90">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>{cred.label}</span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right Column Feature Card */}
          <div className="lg:col-span-5">
            <div className="relative rounded-xl border border-sidebar-border bg-sidebar p-6 sm:p-8 shadow-xl">
              <div className="flex items-center justify-between pb-4 border-b border-sidebar-border">
                <div className="flex items-center gap-2.5">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/20 text-primary">
                    <HardHat className="size-5" />
                  </div>
                  <div>
                    <h2 className="font-default text-base font-semibold text-sidebar-foreground">
                      Access solutions
                    </h2>
                    <p className="text-xs text-sidebar-foreground/70">
                      Tailored to sector specifications
                    </p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-2xs font-semibold bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-border">
                  TG20:21 Compliant
                </Badge>
              </div>

              <div className="mt-6 space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60">
                  Who we work with
                </p>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {targetSectors.map((sector, i) => <div key={sector.title} data-index={i} className="flex items-center gap-2 rounded-lg border border-sidebar-border bg-secondary/60 p-2.5 text-xs text-sidebar-foreground">
                      <Building2 className="size-3.5 text-primary shrink-0" />
                      <span>{sector.title}</span>
                    </div>)}
                </div>
              </div>

              <Separator className="my-6 bg-sidebar-border" />

              <div className="space-y-3">
                <div className="flex items-start gap-3 text-xs text-sidebar-foreground/80">
                  <Truck className="size-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-sidebar-foreground block">
                      Rapid depot dispatch
                    </span>
                    Covering Berkshire, Surrey, Hampshire, Bucks &amp; London daily.
                  </div>
                </div>
                <div className="flex items-start gap-3 text-xs text-sidebar-foreground/80">
                  <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-sidebar-foreground block">
                      Full site audits &amp; handovers
                    </span>
                    Handover certificates and regular 7-day statutory safety inspections.
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-sidebar-border text-center">
                <Button asChild variant="ghost" className="w-full text-xs font-semibold text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                  <Link to="/ProjectsCaseStudiesPage">
                    <span>Explore recent scaffolding projects</span>
                    <ArrowRight className="size-3.5 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}